import React from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

/**
 * Higher-order component that provides router props to class components
 * Compatible with webpack 5 and React Router v6
 *
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {Object} options - Additional options
 * @param {boolean} options.memo - Whether to memoize the wrapper component
 * @param {Object} options.defaultMatch - Default match object for compatibility
 * @returns {React.Component} Wrapped component with router props
 */
export const withRouter = (WrappedComponent, options = {}) => {
  const { memo = false, defaultMatch = { params: {} } } = options;

  const RouterWrapper = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Create history object compatible with React Router v5 API
    const history = {
      push: (path, state) => {
        if (typeof path === "string") {
          navigate(path, { state });
        } else {
          navigate(path.pathname, {
            state: path.state,
            replace: path.replace || false,
          });
        }
      },
      replace: (path, state) => {
        if (typeof path === "string") {
          navigate(path, { state, replace: true });
        } else {
          navigate(path.pathname, {
            state: path.state,
            replace: true,
          });
        }
      },
      go: (n) => window.history.go(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      block: () => () => {}, // No-op for compatibility
      listen: () => () => {}, // No-op for compatibility
      length: window.history.length,
      action: "POP", // Default action
      location: location,
    };

    // Extract params from useParams() hook
    let extractedParams = params || {};

    // Fallback: Extract route params from URL pathname if useParams() didn't return them
    // This handles cases where the route context might not be fully set up
    // Check if areaID is missing, undefined, or empty string
    if (
      (!extractedParams.areaID || extractedParams.areaID === "undefined") &&
      location.pathname
    ) {
      const areaIDMatch = location.pathname.match(
        /\/agent\/property-highlights\/([^\/]+)/
      );
      if (areaIDMatch && areaIDMatch[1]) {
        extractedParams = { ...extractedParams, areaID: areaIDMatch[1] };
      }
    }

    // Ensure params is always an object, and merge with defaultMatch.params
    const mergedParams = { ...defaultMatch.params, ...extractedParams };

    // Create match object compatible with React Router v5 API
    const match = {
      params: mergedParams,
      // Approximate v5's isExact: true when there is no search/hash or trailing path beyond current pathname
      isExact: !location.search && !location.hash,
      path: location.pathname,
      url: location.pathname + location.search,
    };

    return (
      <WrappedComponent
        {...props}
        history={history}
        location={location}
        match={match}
      />
    );
  };

  // Set display name for debugging
  RouterWrapper.displayName = `withRouter(${
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  })`;

  return memo ? React.memo(RouterWrapper) : RouterWrapper;
};

/**
 * Hook for getting router props in functional components
 * Compatible with webpack 5 and React Router v6
 *
 * @returns {Object} Router props object
 */
export const useRouterProps = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const history = {
    push: (path, state) => {
      if (typeof path === "string") {
        navigate(path, { state });
      } else {
        navigate(path.pathname, {
          state: path.state,
          replace: path.replace || false,
        });
      }
    },
    replace: (path, state) => {
      if (typeof path === "string") {
        navigate(path, { state, replace: true });
      } else {
        navigate(path.pathname, {
          state: path.state,
          replace: true,
        });
      }
    },
    go: (n) => window.history.go(n),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    block: () => () => {}, // No-op for compatibility
    listen: () => () => {}, // No-op for compatibility
    length: window.history.length,
    action: "POP", // Default action
    location: location,
  };

  const match = {
    params: params || {},
    isExact: !location.search && !location.hash,
    path: location.pathname,
    url: location.pathname + location.search,
  };

  return {
    history,
    location,
    match,
  };
};

/**
 * Utility function to create router props for components
 * Useful for components that need router props but can't use hooks
 *
 * @param {Function} navigate - useNavigate hook result
 * @param {Object} location - useLocation hook result
 * @param {Object} params - useParams hook result
 * @returns {Object} Router props object
 */
export const createRouterProps = (navigate, location, params = {}) => {
  const history = {
    push: (path, state) => {
      if (typeof path === "string") {
        navigate(path, { state });
      } else {
        navigate(path.pathname, {
          state: path.state,
          replace: path.replace || false,
        });
      }
    },
    replace: (path, state) => {
      if (typeof path === "string") {
        navigate(path, { state, replace: true });
      } else {
        navigate(path.pathname, {
          state: path.state,
          replace: true,
        });
      }
    },
    go: (n) => window.history.go(n),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    block: () => () => {}, // No-op for compatibility
    listen: () => () => {}, // No-op for compatibility
    length: window.history.length,
    action: "POP", // Default action
    location: location,
  };

  const match = {
    params: params || {},
    isExact: !location.search && !location.hash,
    path: location.pathname,
    url: location.pathname + location.search,
  };

  return {
    history,
    location,
    match,
  };
};

export default {
  withRouter,
  useRouterProps,
  createRouterProps,
};
